import { useCallback, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { extractApiErrorMessage } from '../../../pages/AuditLogViewer/utils/utils';
import { LIST_ACTIONS } from '../../../shared/constants';
import {
  useInvertedLocalize,
  useLocalize,
  useTypedSelector,
} from '../../../shared/hooks';
import { type ViolationsActionManagerState } from '../../../shared/types';
import { useLazyGetReportColumnsQuery } from '../../ViolationsReport/data-layer/violationsReportApi';
import { violationsReportActions } from '../../ViolationsReport/data-store/violationsReportSlice';
import {
  buildColumnConfigFromApiColumns,
  buildColumnDisplay,
} from '../../ViolationsReport/utils/columnConfig';
import {
  useAddMitigatingControlMutation,
  useConsultRiskMutation,
  useExtendReviewEndDateMutation,
  useGenerateSyncReportMutation,
  useMitigateControlsEndDateMutation,
  useMitigateMutation,
  useRemediateRiskMutation,
  useRemoveMitigatingControlMutation,
  useReOpenRiskMutation,
} from '../data-layer/violationsTabApi';
import { type SodMitigateProps } from '../data-layer/violationsTabTypes';
import { type BaseFilterState, getFilterPayload } from '../utils';

/**
 * Interface representing a Promise that can be unwrapped
 * @template T The type of value the promise resolves to
 */
interface UnwrappablePromise<T> extends Promise<T> {
  unwrap(): Promise<T>;
}

/**
 * Type definition for the query map object
 * Maps filter keys to their corresponding query functions
 */
type QueryMapType = Record<
  string,
  (params: unknown) => UnwrappablePromise<unknown>
>;

type ActionControllerProps = Pick<
  ViolationsActionManagerState,
  'sodKey' | 'action' | 'isBulkAction'
>;

const OFFSET = 10000;

const useActionController = ({
  action,
  sodKey,
  isBulkAction,
}: ActionControllerProps) => {
  const {
    controls: { tabInAction, activeFilters, searchKey },
    bulkaction: { isBulkChecked, sodKeys },
  } = useTypedSelector((state) => state.aag.violationsTab);
  const dispatch = useDispatch();
  const columnsByReportType = useTypedSelector(
    (state) => state.aag.report.columnsByReportType
  );
  const localize = useLocalize();
  const l = useInvertedLocalize();
  const [triggerGetReportColumns] = useLazyGetReportColumnsQuery();
  const columnsByReportTypeRef = useRef(columnsByReportType);
  const localizeRef = useRef(localize);

  columnsByReportTypeRef.current = columnsByReportType;
  localizeRef.current = localize;

  // Action states
  const [isLoading, setIsLoading] = useState(false);

  // Action map
  const [executeOpenAction] = useReOpenRiskMutation();
  const [executeConsultAction] = useConsultRiskMutation();
  const [executeExtendReviewEndDateAction] = useExtendReviewEndDateMutation();
  const [executeRemediateAction] = useRemediateRiskMutation();
  const [executeRemoveMitigatingContrlAction] =
    useRemoveMitigatingControlMutation();
  const [executeMitigateControlsEndDateAction] =
    useMitigateControlsEndDateMutation();
  const [executeGenerateSyncReportAction] = useGenerateSyncReportMutation();
  const [executeMitigateAction] = useMitigateMutation();
  const [executeAddMitigatingControlAction] = useAddMitigatingControlMutation();

  const actionMap = useMemo(
    () => ({
      [LIST_ACTIONS.REMOVEMC.value]: executeRemoveMitigatingContrlAction,
      [LIST_ACTIONS.REOPEN.value]: executeOpenAction,
      [LIST_ACTIONS.MOVETOINREVIEW.value]: executeConsultAction,
      [LIST_ACTIONS.ADDREVIEWCOMMENTS.value]: executeExtendReviewEndDateAction,
      [LIST_ACTIONS.CHANGEREVIEWENDDATE.value]:
        executeExtendReviewEndDateAction,
      [LIST_ACTIONS.REMEDIATE.value]: executeRemediateAction,
      [LIST_ACTIONS.CHNAGEMCENDDATE.value]:
        executeMitigateControlsEndDateAction,
      [LIST_ACTIONS.GENERATEREPORT.value]: executeGenerateSyncReportAction,
      [LIST_ACTIONS.MITIGATE.value]: executeMitigateAction,
      [LIST_ACTIONS.ADDMC.value]: executeAddMitigatingControlAction,
    }),
    [
      executeRemoveMitigatingContrlAction,
      executeOpenAction,
      executeConsultAction,
      executeExtendReviewEndDateAction,
      executeRemediateAction,
      executeMitigateControlsEndDateAction,
      executeGenerateSyncReportAction,
      executeMitigateAction,
      executeAddMitigatingControlAction,
    ]
  ) as QueryMapType;

  const createBasePayload = useCallback(() => {
    if (isBulkAction) {
      const activeFiltersMap = Object.fromEntries(
        activeFilters.map((f) => [f.value, f.options.map((o) => o.value)])
      );
      const baseFilters = getFilterPayload(
        activeFiltersMap,
        tabInAction ?? undefined,
        searchKey
      );

      if (isBulkChecked) {
        return {
          filters: baseFilters,
          pagination: { offset: 0, max: OFFSET },
          sodIds: {
            in: [] as number[],
            notIn: sodKeys.notIn.map((id) => Number(id)),
          },
        };
      }

      return {
        pagination: { offset: 0, max: OFFSET },
        sodIds: {
          in: sodKeys.in.map((id) => Number(id)),
          notIn: [] as number[],
        },
      };
    }

    return {
      pagination: { offset: 0, max: OFFSET },
      sodIds: {
        in: [Number(sodKey)],
        notIn: [] as number[],
      },
    };
  }, [
    activeFilters,
    isBulkAction,
    isBulkChecked,
    searchKey,
    sodKey,
    sodKeys.in,
    sodKeys.notIn,
    tabInAction,
  ]);

  const handleErrorMessage = useCallback(
    (error: unknown) => {
      const typedError = error as {
        status?: number;
        data?: { message?: string; response?: { data?: { message?: string } } };
      };

      if (typedError.status && typedError.status === 403) {
        return l('You do not have permission to perform this action.');
      }

      if (typedError.status) {
        return (
          typedError.data?.message ??
          typedError.data?.response?.data?.message ??
          l('Bad Request')
        );
      }

      return l(
        'An unexpected error occurred. Please contact system administrator.'
      );
    },
    [l]
  );

  // Action fns
  const handleOpenAction = useCallback(
    async (comment: string) => {
      setIsLoading(true);

      try {
        const actionFn = actionMap[action];
        const controller = actionFn({
          ...createBasePayload(),
          comment,
        });
        const response = await controller.unwrap();

        return response;
      } catch (error) {
        const errorMessage = handleErrorMessage(error);

        throw Error(errorMessage);
      } finally {
        setIsLoading(false);
      }
    },
    [createBasePayload, handleErrorMessage, actionMap, action]
  );

  const handleConsultAction = useCallback(
    async (
      comment: string,
      config: { startDate?: string; endDate?: string } = {}
    ) => {
      setIsLoading(true);

      try {
        const actionFn = actionMap[action];
        const controller = actionFn({
          ...createBasePayload(),
          data: {
            startDate: config.startDate ?? undefined,
            endDate: config.endDate ?? undefined,
            comment,
          },
        });
        const response = await controller.unwrap();

        return response;
      } catch (error) {
        const errorMessage = handleErrorMessage(error);

        throw Error(errorMessage);
      } finally {
        setIsLoading(false);
      }
    },
    [createBasePayload, handleErrorMessage, actionMap, action]
  );

  const handleReviewExtendDateAction = useCallback(
    async (comment: string, config: { endDate?: string } = {}) => {
      setIsLoading(true);

      try {
        const actionFn = actionMap[action];
        const controller = actionFn({
          ...createBasePayload(),
          data: {
            endDate: config.endDate ?? undefined,
            comment,
          },
        });
        const response = await controller.unwrap();

        return response;
      } catch (error) {
        const errorMessage = handleErrorMessage(error);

        throw Error(errorMessage);
      } finally {
        setIsLoading(false);
      }
    },
    [createBasePayload, handleErrorMessage, actionMap, action]
  );

  const handleRemediateAction = useCallback(
    async (
      comment: string,
      config: {
        functionId?: number;
        violationId?: string;
        remediateOption?: string;
      } = {}
    ) => {
      setIsLoading(true);

      const payload = {
        violationId: config.violationId,
        payload: {
          comments: comment,
          functionId: config.functionId,
          remediateOption: config.remediateOption,
        },
      };

      try {
        const actionFn = actionMap[action];
        const controller = actionFn(payload);
        const response = await controller.unwrap();

        return response;
      } catch (error) {
        const errorMessage = handleErrorMessage(error);

        throw Error(errorMessage);
      } finally {
        setIsLoading(false);
      }
    },
    [handleErrorMessage, actionMap, action]
  );

  const handleRemoveMitigatingControl = useCallback(
    async (
      comment: string,
      config: { mitigatingControlKeys?: number[]; violationId?: number } = {}
    ) => {
      setIsLoading(true);

      const payload = {
        violationId: config.violationId ?? '',
        mcKeys: config.mitigatingControlKeys ?? [],
        comments: comment,
      };

      try {
        const actionFn = actionMap[action];
        const controller = actionFn(payload);
        const response = await controller.unwrap();

        return response;
      } catch (error) {
        const errorMessage = handleErrorMessage(error);

        throw Error(errorMessage);
      } finally {
        setIsLoading(false);
      }
    },
    [handleErrorMessage, action, actionMap]
  );

  const handleMitigateControlsEndDateAction = useCallback(
    async (
      comment: string,
      config: { endDate?: string; mcKeys?: number[] } = {}
    ) => {
      setIsLoading(true);

      try {
        const actionFn = actionMap[action];
        const controller = actionFn({
          ...createBasePayload(),
          mcKeys: config.mcKeys ?? undefined,
          endDate: config.endDate ?? undefined,
          comment,
        });
        const response = await controller.unwrap();

        return response;
      } catch (error) {
        const errorMessage = handleErrorMessage(error);

        throw Error(errorMessage);
      } finally {
        setIsLoading(false);
      }
    },
    [createBasePayload, handleErrorMessage, actionMap, action]
  );

  const handleGenerateSyncReportAction = useCallback(
    async (_: string, config: { violationId?: string } = {}) => {
      setIsLoading(true);

      let columnsData = columnsByReportTypeRef.current?.PERMISSIONS;

      if (!columnsData) {
        try {
          const response = await triggerGetReportColumns(
            'PERMISSIONS'
          ).unwrap();

          dispatch(
            violationsReportActions.setColumnsForReportType({
              reportType: 'PERMISSIONS',
              columns: response,
            })
          );
          columnsData = response;
        } catch (columnsError) {
          setIsLoading(false);

          const apiMessage = extractApiErrorMessage(columnsError, '');

          throw new Error(
            apiMessage
              ? `${apiMessage} - Unable to load report columns. Please try again.`
              : 'Unable to load report columns. Please try again.'
          );
        }
      }

      const columnConfig = buildColumnConfigFromApiColumns(columnsData);
      const columnDisplay = buildColumnDisplay(
        columnConfig.selectedColumns,
        columnsData,
        localizeRef.current
      );

      const payload = {
        reportType: 'PERMISSIONS' as const,
        format: 'EXCEL' as const,
        locale: 'en' as const,
        userId: 'abc12460',
        columnConfig: {
          ...columnConfig,
          columnDisplay,
        },
        filters: {
          violationIds: [config.violationId ?? ''],
        },
      };

      try {
        const actionFn = actionMap[action];
        const controller = actionFn(payload);
        const response = await controller.unwrap();

        return response;
      } catch (error) {
        const errorMessage = handleErrorMessage(error);

        throw Error(errorMessage);
      } finally {
        setIsLoading(false);
      }
    },
    [action, actionMap, dispatch, triggerGetReportColumns, handleErrorMessage]
  );

  const createMitigateBasePayload = useCallback(
    (
      selectedControls?: {
        id: number;
        startDate: string;
        endDate: string;
        comments: string;
      }[]
    ) => {
      const activeFiltersMap = Object.fromEntries(
        activeFilters.map((f) => [f.value, f.options.map((o) => o.value)])
      );
      const baseFilters = getFilterPayload(
        activeFiltersMap,
        tabInAction ?? undefined,
        searchKey
      );

      delete (baseFilters as BaseFilterState).sodStatuses;

      const payload: SodMitigateProps = {
        allMitigatingControls: selectedControls ?? [],
        filters: baseFilters,
      } as SodMitigateProps;

      if (isBulkAction) {
        if (isBulkChecked) {
          payload.sodIds = {
            in: [] as number[],
            notIn: sodKeys.notIn.map((id) => Number(id)),
          };
        } else {
          payload.sodIds = {
            in: sodKeys.in.map((id) => Number(id)),
            notIn: [] as number[],
          };
          payload.allMitigatingControls = selectedControls ?? [];
        }
      } else {
        payload.sodIds = {
          in: [Number(sodKey)],
          notIn: [] as number[],
        };
      }

      return payload;
    },
    [
      activeFilters,
      isBulkAction,
      isBulkChecked,
      searchKey,
      sodKey,
      sodKeys.in,
      sodKeys.notIn,
      tabInAction,
    ]
  );

  const handleMitigateAction = useCallback(
    async (
      _: string,
      config: {
        selectedControls?: {
          id: number;
          startDate: string;
          endDate: string;
          comments: string;
        }[];
      } = {}
    ) => {
      setIsLoading(true);

      try {
        const actionFn = actionMap[action];
        const controller = actionFn({
          ...createMitigateBasePayload(config.selectedControls),
        });
        const response = await controller.unwrap();

        return response;
      } catch (error) {
        const errorMessage = handleErrorMessage(error);

        throw Error(errorMessage);
      } finally {
        setIsLoading(false);
      }
    },
    [actionMap, action, createMitigateBasePayload, handleErrorMessage]
  );

  const handleAddMitigatingControlAction = useCallback(
    async (
      _: string,
      config: {
        selectedControls?: {
          id: number;
          startDate: string;
          endDate: string;
          comments: string;
        }[];
      } = {}
    ) => {
      setIsLoading(true);

      try {
        const actionFn = actionMap[action];
        const controller = actionFn({
          ...createMitigateBasePayload(config.selectedControls),
        });
        const response = await controller.unwrap();

        return response;
      } catch (error) {
        const errorMessage = handleErrorMessage(error);

        throw Error(errorMessage);
      } finally {
        setIsLoading(false);
      }
    },
    [actionMap, action, createMitigateBasePayload, handleErrorMessage]
  );

  const callMap = useMemo(
    () => ({
      [LIST_ACTIONS.REMOVEMC.value]: handleRemoveMitigatingControl,
      [LIST_ACTIONS.REOPEN.value]: handleOpenAction,
      [LIST_ACTIONS.MOVETOINREVIEW.value]: handleConsultAction,
      [LIST_ACTIONS.ADDREVIEWCOMMENTS.value]: handleReviewExtendDateAction,
      [LIST_ACTIONS.CHANGEREVIEWENDDATE.value]: handleReviewExtendDateAction,
      [LIST_ACTIONS.REMEDIATE.value]: handleRemediateAction,
      [LIST_ACTIONS.CHNAGEMCENDDATE.value]: handleMitigateControlsEndDateAction,
      [LIST_ACTIONS.GENERATEREPORT.value]: handleGenerateSyncReportAction,
      [LIST_ACTIONS.MITIGATE.value]: handleMitigateAction,
      [LIST_ACTIONS.ADDMC.value]: handleAddMitigatingControlAction,
    }),
    [
      handleRemoveMitigatingControl,
      handleOpenAction,
      handleConsultAction,
      handleReviewExtendDateAction,
      handleRemediateAction,
      handleMitigateControlsEndDateAction,
      handleGenerateSyncReportAction,
      handleMitigateAction,
      handleAddMitigatingControlAction,
    ]
  );

  const actionFn =
    action in callMap
      ? callMap[action]
      : () => Promise.reject(new Error('No action function found'));

  return { actionFn, isLoading };
};

// eslint-disable-next-line import/prefer-default-export
export { useActionController };
